import React from 'react';
import { UserProfile, OtherIncomeSource } from '../../types/advanced-retirement';
import FormattedInput from '../FormattedInput';

interface OtherIncomeSectionProps {
  userProfile: UserProfile;
  handleInputChange: (field: keyof UserProfile, value: any) => void;
}

const TAX_TREATMENTS = [
  { value: 'regular', label: 'Regular income (fully taxable)' },
  { value: 'capital-gains', label: 'Property or asset sale (50% of the gain is taxable)' },
  { value: 'non-taxable', label: 'Tax-free (principal residence sale, inheritance, gift)' }
];

// Older saved cards used 'Steady' / 'increasing' / 'decreasing'; the engine models one-time or ongoing only
const isOneTime = (source: OtherIncomeSource) => source.profile === 'one time';

// A cleared number field parses to NaN; keep the previous age so the card stays valid
const parseAge = (value: string, fallback: number | undefined) => {
  const age = parseInt(value, 10);
  return Number.isNaN(age) ? fallback : age;
};

const OtherIncomeSection: React.FC<OtherIncomeSectionProps> = ({
  userProfile,
  handleInputChange
}) => {
  const sourcesField = (isSpouse: boolean): keyof UserProfile => isSpouse ? 'spouseOtherIncomeSources' : 'otherIncomeSources';
  const sourcesFor = (isSpouse: boolean): OtherIncomeSource[] =>
    (isSpouse ? userProfile.spouseOtherIncomeSources : userProfile.otherIncomeSources) || [];

  const addOtherIncome = (isSpouse: boolean) => {
    const current = sourcesFor(isSpouse);
    const newIncome: OtherIncomeSource = {
      id: Date.now().toString(),
      amount: 0,
      startAge: 65,
      profile: 'one time',
      incomeType: 'regular',
      costBase: 0,
      description: `${isSpouse ? 'Spouse ' : ''}Other Income #${current.length + 1}`
    };
    handleInputChange(sourcesField(isSpouse), [...current, newIncome]);
  };

  const removeOtherIncome = (id: string, isSpouse: boolean) => {
    handleInputChange(sourcesField(isSpouse), sourcesFor(isSpouse).filter(source => source.id !== id));
  };

  const updateOtherIncome = (id: string, field: keyof OtherIncomeSource, value: any, isSpouse: boolean) => {
    const updated = sourcesFor(isSpouse).map(source => source.id === id ? { ...source, [field]: value } : source);
    handleInputChange(sourcesField(isSpouse), updated);
  };

  const renderCard = (source: OtherIncomeSource, isSpouse: boolean) => (
    <div key={source.id} className="income-source-item">
      <div className="income-source-header">
        <h5>{source.description}</h5>
        <button
          type="button"
          className="remove-income-btn"
          onClick={() => removeOtherIncome(source.id, isSpouse)}
          disabled={sourcesFor(isSpouse).length <= 1}
        >
          ✕
        </button>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Amount</label>
          <FormattedInput
            value={source.amount}
            onChange={(value) => updateOtherIncome(source.id, 'amount', value, isSpouse)}
            min={0}
            step={1000}
            formatOptions={{ type: 'currency', decimalPlaces: 0 }}
          />
        </div>
        <div className="form-group">
          <label>Age Start</label>
          <input
            type="number"
            value={source.startAge}
            onChange={(e) => updateOtherIncome(source.id, 'startAge', parseAge(e.target.value, source.startAge), isSpouse)}
            min="18" max="105"
          />
        </div>
        <div className="form-group">
          <label>Profile</label>
          <select
            value={isOneTime(source) ? 'one time' : 'ongoing'}
            onChange={(e) => updateOtherIncome(source.id, 'profile', e.target.value, isSpouse)}
          >
            <option value="one time">One Time</option>
            <option value="ongoing">Ongoing</option>
          </select>
        </div>
        {!isOneTime(source) && (
          <div className="form-group">
            <label>Age End</label>
            <input
              type="number"
              value={source.endAge ?? ''}
              placeholder="For life"
              onChange={(e) => updateOtherIncome(source.id, 'endAge', e.target.value === '' ? undefined : parseAge(e.target.value, source.endAge), isSpouse)}
              min={source.startAge} max="105"
            />
          </div>
        )}
        <div className="form-group">
          <label>Tax Treatment</label>
          <select
            value={source.incomeType || 'regular'}
            onChange={(e) => updateOtherIncome(source.id, 'incomeType', e.target.value, isSpouse)}
          >
            {TAX_TREATMENTS.map(treatment => (
              <option key={treatment.value} value={treatment.value}>{treatment.label}</option>
            ))}
          </select>
        </div>
        {source.incomeType === 'capital-gains' && (
          <div className="form-group">
            <label>Cost Base</label>
            <FormattedInput
              value={source.costBase || 0}
              onChange={(value) => updateOtherIncome(source.id, 'costBase', value, isSpouse)}
              min={0}
              step={1000}
              formatOptions={{ type: 'currency', decimalPlaces: 0 }}
            />
          </div>
        )}
        <div className="form-group">
          <label>Description</label>
          <input
            type="text"
            value={source.description || ''}
            onChange={(e) => updateOtherIncome(source.id, 'description', e.target.value, isSpouse)}
            placeholder="e.g., Home sale, Cottage sale, Rental Income"
          />
        </div>
      </div>
    </div>
  );

  const renderSources = (isSpouse: boolean) => (
    <div className="income-sources-section">
      <h4>{isSpouse ? 'Spouse Other Income Sources' : 'My Other Income Sources'}</h4>
      {sourcesFor(isSpouse).map(source => renderCard(source, isSpouse))}
      <button type="button" className="add-income-btn" onClick={() => addOtherIncome(isSpouse)}>
        {isSpouse ? '+ ADD SPOUSE OTHER INCOME' : '+ ADD OTHER INCOME'}
      </button>
    </div>
  );

  return (
    <div className="form-section">
      <h3>Other Income</h3>
      <p style={{ fontSize: '0.9rem', color: '#6c757d' }}>
        Amounts are in today's dollars and grow with inflation until the year received, one-time amounts included.
        Selling your home? Enter today's value as a one-time Tax-free amount at the age you sell (or the share you free up if you downsize).
        Selling a cottage or rental? Choose Property sale and enter what you paid as the cost base.
      </p>
      {renderSources(false)}
      {userProfile.spouseAge && renderSources(true)}
    </div>
  );
};

export default OtherIncomeSection;
